import { resolveGoalTreeLink, resolveTaskParents } from '@griply/utils';
import Typography from '@mui/material/Typography';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Target, X } from 'shared/assets/icons';
import { GoalTreeMenu } from 'shared/components/ui/goal-tree-menu';
import { Icon } from 'shared/components/ui/icon';
import { Colors, Sizes } from 'shared/components/ui/icon-button';
import { useCloseTaskDetail } from 'shared/contexts/task-detail';
import { Paths } from 'shared/routes/paths';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LinkOptions } from 'shared/types/task';
import { getGoalDetailUrl } from 'shared/utils/get-goal-detail-url';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

import { useTaskDetailShortcut } from '../../hooks/use-task-detail-shortcut';
import { useTaskDetailSubmenu } from '../../hooks/use-task-detail-submenu';
import { useData } from '../../use-data';
import * as Styled from './meta-linked-item.style';

export type MetaLinkedItemProps = {
  goalIds: ID[];
  lifeAreaIds: ID[];
  onChange: (link: { id: ID; type: LinkOptions } | null) => void;
  multiSelect?: boolean;
  isCreate?: boolean;
};

export const MetaLinkedItem: React.FC<MetaLinkedItemProps> = ({
  goalIds,
  lifeAreaIds,
  onChange,
  multiSelect,
  isCreate,
}) => {
  const { goals, lifeAreas } = useData();
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { menuOpen, openMenu, closeMenu } = useTaskDetailSubmenu();
  const navigate = useNavigate();
  const closeTaskDetail = useCloseTaskDetail();

  const items = useMemo(
    () =>
      resolveTaskParents({ goalIds, lifeAreaIds }, { goals, lifeAreas }).map(
        (parent) =>
          parent.kind === LinkOptions.Goal ? parent.goal : parent.lifeArea,
      ),
    [goalIds, goals, lifeAreaIds, lifeAreas],
  );

  // Only a lone link has a destination to deep-link to; a multi-select row is a picker throughout.
  const linkTarget = !multiSelect && items.length === 1 ? items[0] : undefined;
  const hasItems = !!items.length;

  const _onChange = (link: { id: ID; type: LinkOptions } | null) => {
    onChange(link);
    if (!multiSelect || !link) {
      closeMenu();
    }
  };

  // No selection while unlinked — `{ noLifeArea: true }` would grey out the bucket that doubles as clear.
  const currentSelection = useMemo(() => {
    if (goalIds[0]) {
      return { goalId: goalIds[0] };
    }
    return lifeAreaIds[0] ? { lifeAreaId: lifeAreaIds[0] } : undefined;
  }, [goalIds, lifeAreaIds]);

  const handleSelect = (ancestorIds: ID[], lifeAreaId: ID | null) =>
    _onChange(resolveGoalTreeLink(ancestorIds, lifeAreaId));

  const handleToggleGoal = (goalId: ID) =>
    _onChange({ id: goalId, type: LinkOptions.Goal });

  const handleToggleLifeArea = (lifeAreaId: ID) =>
    _onChange({ id: lifeAreaId, type: LinkOptions.LifeArea });

  const onShortcut = useCallback(
    (e: KeyboardEvent) => {
      // Don't trigger if Shift is pressed
      if (e.shiftKey) {
        return;
      }

      if (!menuOpen) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        openMenu();
      }
    },
    [menuOpen, openMenu],
  );

  useTaskDetailShortcut(['KeyG'], onShortcut, {
    ignoreInputFields: true,
    capture: true,
  });

  const onClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    closeMenu();
  };

  const navigateToItem = () => {
    if (!linkTarget) {
      return;
    }

    navigate(
      'lifeAreaId' in linkTarget
        ? getGoalDetailUrl(linkTarget)
        : replaceUrlParams(Paths.LifeAreaDetail, { id: linkTarget.id }),
    );
    closeTaskDetail();
  };

  return (
    <>
      {/* @ts-ignore */}
      <Styled.Container
        ref={buttonRef}
        onClick={linkTarget && !isCreate ? undefined : openMenu}
        $hasValue={hasItems}
        as={linkTarget ? 'div' : undefined}
        $selected={menuOpen}
      >
        <Styled.Content $isPlaceholder={!hasItems}>
          <Styled.IconContainer>
            <Icon icon={Target} />
          </Styled.IconContainer>

          {!hasItems && (
            <Typography variant="body2">
              {t('dialogs.task-detail.meta.linked-item.placeholder')}
            </Typography>
          )}

          {hasItems && !linkTarget && (
            <Styled.Link as="span">
              {items.map(({ name }) => name).join(', ')}
            </Styled.Link>
          )}

          {!!linkTarget && (
            <Styled.LinkContainer>
              <Styled.Link
                onClick={isCreate ? undefined : navigateToItem}
                as={isCreate ? 'span' : undefined}
              >
                {linkTarget.name}
              </Styled.Link>
              {!isCreate && (
                <Styled.ChevronContainer>
                  <Icon icon={ChevronLeft} />
                </Styled.ChevronContainer>
              )}
            </Styled.LinkContainer>
          )}
        </Styled.Content>

        {hasItems && (
          <Styled.IconButton
            icon={X}
            onClick={onClear}
            color={Colors.Default}
            size={Sizes.Small}
          />
        )}
      </Styled.Container>

      {menuOpen && (
        <GoalTreeMenu
          referenceElement={buttonRef}
          goals={goals as Goal[]}
          lifeAreas={lifeAreas}
          onSelect={handleSelect}
          onClose={closeMenu}
          hideNoLifeArea={multiSelect}
          {...(multiSelect
            ? {
                selectedGoalIds: goalIds,
                selectedLifeAreaIds: lifeAreaIds,
                onToggleGoal: handleToggleGoal,
                onToggleLifeArea: handleToggleLifeArea,
              }
            : { selection: currentSelection })}
        />
      )}
    </>
  );
};
